/**
 * Community notifications inbox — /community/notifications
 *
 * Full inbox view. Lists all notifications, with an "unread only"
 * filter, click-through to the relevant thread/conversation, and
 * mark-all-read. Also surfaces the per-user community-email-opt-out
 * toggle (DEC-0040).
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  fetchEmailPreferences,
  fetchNotifications,
  markNotificationsRead,
  updateEmailPreferences,
  type EmailPreferences,
  type NotificationRead,
  type NotificationType,
} from "@/lib/community-api";

const PAGE_SIZE = 20;

type InboxState =
  | { kind: "loading" }
  | {
      kind: "ready";
      notifications: NotificationRead[];
      unreadCount: number;
      total: number;
      nextOffset: number | null;
    }
  | { kind: "not_member" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function typeCopy(type: NotificationType, actorName: string): string {
  switch (type) {
    case "reply":
      return `${actorName} replied to your thread`;
    case "mention":
      return `${actorName} mentioned you`;
    case "reaction":
      return `${actorName} reacted to your post`;
    case "moderation":
      return "A moderation action affected your post";
    case "announcement":
      return "New announcement";
    case "dm":
      return `${actorName} sent you a direct message`;
  }
}

function targetLinkFor(n: NotificationRead): string | null {
  if (n.type === "dm" && n.target_id) {
    return `/community/messages/${n.target_id}`;
  }
  if (n.thread_id) {
    return `/community/t/${n.thread_id}`;
  }
  return null;
}

export interface CommunityNotificationsPageProps {
  fetchFn?: typeof fetchNotifications;
  markReadFn?: typeof markNotificationsRead;
  fetchPrefsFn?: typeof fetchEmailPreferences;
  updatePrefsFn?: typeof updateEmailPreferences;
  onUnauthenticated?: (returnTo: string) => void;
}

export function CommunityNotificationsPage({
  fetchFn = fetchNotifications,
  markReadFn = markNotificationsRead,
  fetchPrefsFn = fetchEmailPreferences,
  updatePrefsFn = updateEmailPreferences,
  onUnauthenticated = redirectToLogin,
}: CommunityNotificationsPageProps = {}): React.JSX.Element {
  const [state, setState] = useState<InboxState>({ kind: "loading" });
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [prefs, setPrefs] = useState<EmailPreferences | null>(null);
  const [prefsSavedAt, setPrefsSavedAt] = useState<number | null>(null);
  const [prefsError, setPrefsError] = useState<string | null>(null);

  const reload = async (
    nextOffset: number,
    nextUnreadOnly: boolean,
  ): Promise<void> => {
    try {
      setState({ kind: "loading" });
      const resp = await fetchFn({
        unread: nextUnreadOnly,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      setState({
        kind: "ready",
        notifications: resp.notifications,
        unreadCount: resp.unread_count,
        total: resp.total,
        nextOffset: resp.next_offset,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthenticated("/community/notifications");
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setState({ kind: "not_member" });
        return;
      }
      const msg =
        err instanceof Error ? err.message : "Could not load your inbox.";
      setState({ kind: "error", message: msg });
    }
  };

  useEffect(() => {
    void reload(offset, unreadOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, unreadOnly]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await fetchPrefsFn();
        if (!cancelled) setPrefs(p);
      } catch {
        // Prefs load failure is non-fatal — user just won't see the toggle.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPrefsFn]);

  const handleMarkAllRead = async (): Promise<void> => {
    try {
      await markReadFn();
      await reload(offset, unreadOnly);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Could not mark notifications as read.";
      setState((s) =>
        s.kind === "ready" ? { kind: "error", message: msg } : s,
      );
    }
  };

  const handleTogglePrefs = async (optOut: boolean): Promise<void> => {
    setPrefsError(null);
    try {
      const updated = await updatePrefsFn({ community_email_opt_out: optOut });
      setPrefs(updated);
      setPrefsSavedAt(Date.now());
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Could not update your email preferences.";
      setPrefsError(msg);
    }
  };

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading inbox…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-muted-foreground">
          The community is open to active learners and certified
          practitioners.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community" className="hover:text-foreground">
          Community
        </Link>{" "}
        <span aria-hidden="true">›</span> Notifications
      </nav>

      <header className="mt-3 mb-6 flex items-baseline justify-between gap-4">
        <h1 className="text-3xl font-bold text-foreground">
          Notifications
          {state.unreadCount > 0 && (
            <span className="ml-3 inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground align-middle">
              {state.unreadCount} unread
            </span>
          )}
        </h1>
        <div className="flex items-center gap-2 text-xs">
          <label className="flex items-center gap-1 text-muted-foreground">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => {
                setOffset(0);
                setUnreadOnly(e.target.checked);
              }}
            />
            Unread only
          </label>
          {state.unreadCount > 0 && (
            <button
              type="button"
              onClick={() => {
                void handleMarkAllRead();
              }}
              className="rounded-md border border-border px-3 py-1 font-medium hover:bg-muted"
            >
              Mark all read
            </button>
          )}
        </div>
      </header>

      {state.notifications.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {unreadOnly
            ? "Nothing unread."
            : "Nothing here yet. When someone replies, mentions, or DMs you, it'll show up here."}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-white">
          {state.notifications.map((n) => (
            <li key={n.id}>
              <NotificationRow notification={n} />
            </li>
          ))}
        </ul>
      )}

      {(offset > 0 || state.nextOffset !== null) && (
        <nav className="mt-6 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => {
              setOffset(Math.max(0, offset - PAGE_SIZE));
            }}
            disabled={offset === 0}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            ← Previous
          </button>
          <p className="text-xs text-muted-foreground">
            {offset + 1}–{Math.min(offset + state.notifications.length, state.total)} of{" "}
            {state.total}
          </p>
          <button
            type="button"
            onClick={() => {
              if (state.nextOffset !== null) setOffset(state.nextOffset);
            }}
            disabled={state.nextOffset === null}
            className="rounded-md border border-border px-3 py-1 hover:bg-muted disabled:opacity-50"
          >
            Next →
          </button>
        </nav>
      )}

      {prefs && (
        <section className="mt-12 rounded-lg border border-border bg-muted/30 p-4">
          <h2 className="text-base font-semibold text-foreground">
            Email preferences
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            The community sends a weekly digest of activity you missed
            (replies, mentions, reactions). Toggle it off if you'd prefer
            in-app only.
          </p>
          <label className="mt-3 flex items-start gap-3 text-sm text-foreground">
            <input
              type="checkbox"
              checked={prefs.community_email_opt_out}
              onChange={(e) => {
                void handleTogglePrefs(e.target.checked);
              }}
              disabled={prefs.email_suppress_all}
              className="mt-1"
            />
            <span>
              <strong>Opt out of community emails.</strong>{" "}
              <span className="text-muted-foreground">
                In-app notifications still work.
              </span>
              {prefs.email_suppress_all && (
                <span className="mt-1 block text-xs text-muted-foreground">
                  (All emails are currently suppressed for your account; this
                  toggle is inactive.)
                </span>
              )}
            </span>
          </label>
          {prefsError && (
            <p className="mt-2 text-xs text-red-700" role="alert">
              {prefsError}
            </p>
          )}
          {prefsSavedAt !== null && prefsError === null && (
            <p className="mt-2 text-xs text-green-700" role="status">
              Saved.
            </p>
          )}
        </section>
      )}
    </section>
  );
}

function NotificationRow({
  notification,
}: {
  notification: NotificationRead;
}): React.JSX.Element {
  const actorName = notification.actor?.display_name ?? "A member";
  const copy = typeCopy(notification.type, actorName);
  const link = targetLinkFor(notification);
  const inner = (
    <div className="flex items-baseline gap-3">
      {!notification.is_read && (
        <span
          className="mt-1 inline-block h-2 w-2 rounded-full bg-primary"
          aria-hidden="true"
        />
      )}
      <div className="flex-1">
        <p
          className={`text-sm ${
            notification.is_read
              ? "text-muted-foreground"
              : "font-medium text-foreground"
          }`}
        >
          {copy}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {formatRelative(notification.created_at)}
        </p>
      </div>
    </div>
  );
  if (link) {
    return (
      <Link to={link} className="block px-4 py-3 hover:bg-muted/40">
        {inner}
      </Link>
    );
  }
  return <div className="px-4 py-3">{inner}</div>;
}
